import { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import ProtectedLink from "../../components/ProtectedLink";

const TYPES = [
  { id: "", label: "Tous" },
  { id: "entry",      label: "Entrées",      icon: "📥", color: "#1a9e72" },
  { id: "exit",       label: "Sorties",      icon: "📤", color: "#e24b4a" },
  { id: "adjustment", label: "Ajustements",  icon: "🔧", color: "#ba7517" },
  { id: "return",     label: "Retours",      icon: "↩️",  color: "#8b5cf6" },
  { id: "inventory",  label: "Inventaire",   icon: "📋", color: "#185fa5" },
  { id: "transfer",   label: "Transferts",   icon: "🔄", color: "#0891b2" },
];

const TYPE_COLOR = { entry: "#1a9e72", exit: "#e24b4a", adjustment: "#ba7517", return: "#8b5cf6", inventory: "#185fa5", transfer: "#0891b2" };
const TYPE_ICON  = { entry: "📥", exit: "📤", adjustment: "🔧", return: "↩️", inventory: "📋", transfer: "🔄" };
const TYPE_LABEL = { entry: "Entrée", exit: "Sortie", adjustment: "Ajustement", return: "Retour client", inventory: "Inventaire", transfer: "Transfert" };

export default function MovementsTab({ currentCompanyId, products = [], warehouses = [], companies = [] }) {
  const [movements, setMovements]         = useState([]);
  const [loading, setLoading]             = useState(true);
  const [typeFilter, setTypeFilter]       = useState("");
  const [showModal, setShowModal]         = useState(false);
  const [mvType, setMvType]               = useState("entry");
  const [form, setForm]                   = useState({ product_id: "", quantity: "", unit_price: "", batch_number: "", reason: "", reference: "", warehouse_id: "", from_warehouse_id: "", to_warehouse_id: "", expiry_date: "", purchase_date: "", supplier_id: "", counted_quantity: "" });
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState("");
  const [suppliers, setSuppliers]         = useState([]);

  // Inter-company transfer state
  const [showInterModal, setShowInterModal] = useState(false);
  const [interForm, setInterForm]           = useState({ dest_company_id: "", source_product_id: "", dest_product_id: "", quantity: "", unit_price: "", reason: "Transfert inter-sociétés", notes: "" });
  const [destProducts, setDestProducts]     = useState([]);
  const [interTransfers, setInterTransfers] = useState([]);
  const [showInterTab, setShowInterTab]     = useState(false);
  const [interSaving, setInterSaving]       = useState(false);
  const [interError, setInterError]         = useState("");

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.stocks.movements(currentCompanyId, { type: typeFilter, limit: 100 })
      .then(r => setMovements(Array.isArray(r) ? r : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId, typeFilter]);

  const loadInterTransfers = useCallback(() => {
    if (!currentCompanyId) return;
    api.stocks.interCompanyTransfers(currentCompanyId)
      .then(r => setInterTransfers(Array.isArray(r) ? r : []))
      .catch(console.error);
  }, [currentCompanyId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (showInterTab) loadInterTransfers(); }, [showInterTab, loadInterTransfers]);

  useEffect(() => {
    if (currentCompanyId) api.stocks.suppliers(currentCompanyId).then(setSuppliers).catch(() => {});
  }, [currentCompanyId]);

  // Chargement produits société destination
  useEffect(() => {
    if (!interForm.dest_company_id) { setDestProducts([]); return; }
    api.stocks.products(interForm.dest_company_id, { limit: 500 })
      .then(r => setDestProducts(r.products || []))
      .catch(() => setDestProducts([]));
  }, [interForm.dest_company_id]);

  const openModal = (type) => {
    setMvType(type);
    setForm({ product_id: "", quantity: "", unit_price: "", batch_number: "", reason: "", reference: "", warehouse_id: "", from_warehouse_id: "", to_warehouse_id: "", expiry_date: "", purchase_date: "", supplier_id: "" });
    setError("");
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      const base = { product_id: parseInt(form.product_id), company_id: currentCompanyId };
      if (mvType === "entry") {
        await api.stocks.entry({ ...base, quantity: parseFloat(form.quantity), unit_price: parseFloat(form.unit_price) || 0, batch_number: form.batch_number || undefined, supplier_id: form.supplier_id || undefined, reason: form.reason || "Entrée manuelle", reference: form.reference || undefined, warehouse_id: form.warehouse_id || undefined, expiry_date: form.expiry_date || undefined, purchase_date: form.purchase_date || undefined });
      } else if (mvType === "exit") {
        await api.stocks.exit({ ...base, quantity: parseFloat(form.quantity), reason: form.reason || "Sortie", reference: form.reference || undefined, warehouse_id: form.warehouse_id || undefined });
      } else if (mvType === "adjustment") {
        await api.stocks.adjustment({ ...base, quantity: parseFloat(form.quantity), reason: form.reason || "Ajustement", reference: form.reference || undefined, warehouse_id: form.warehouse_id || undefined });
      } else if (mvType === "return") {
        await api.stocks.return({ ...base, quantity: parseFloat(form.quantity), unit_price: parseFloat(form.unit_price) || undefined, reason: form.reason || "Retour client", reference: form.reference || undefined, warehouse_id: form.warehouse_id || undefined });
      } else if (mvType === "transfer") {
        await api.stocks.transfer({ ...base, quantity: parseFloat(form.quantity), from_warehouse_id: parseInt(form.from_warehouse_id), to_warehouse_id: parseInt(form.to_warehouse_id), reason: form.reason || "Transfert" });
      }
      setShowModal(false); load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleInterSubmit = async (e) => {
    e.preventDefault(); setInterError(""); setInterSaving(true);
    try {
      await api.stocks.interCompanyTransfer({
        source_company_id: currentCompanyId,
        dest_company_id:   parseInt(interForm.dest_company_id),
        source_product_id: parseInt(interForm.source_product_id),
        dest_product_id:   interForm.dest_product_id ? parseInt(interForm.dest_product_id) : undefined,
        quantity:          parseFloat(interForm.quantity),
        unit_price:        parseFloat(interForm.unit_price) || 0,
        reason:            interForm.reason || "Transfert inter-sociétés",
        notes:             interForm.notes || undefined,
      });
      setShowInterModal(false);
      load();
      loadInterTransfers();
    } catch (err) { setInterError(err.message); }
    finally { setInterSaving(false); }
  };

  const selectedProduct = products.find(p => p.id === parseInt(form.product_id));
  const otherCompanies  = companies.filter(c => String(c.id) !== String(currentCompanyId));

  const STATUS_COLOR = { completed: "#1a9e72", pending: "#ba7517", cancelled: "#e24b4a" };
  const STATUS_LABEL = { completed: "Complété", pending: "En attente", cancelled: "Annulé" };

  return (
    <div>
      {/* Sous-onglets : Mouvements / Transferts inter-sociétés */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16 }}>
        <button onClick={() => setShowInterTab(false)} style={{ padding: "6px 14px", borderRadius: 8, fontSize: 13, fontWeight: showInterTab ? 500 : 700, cursor: "pointer", border: `1px solid ${!showInterTab ? "var(--g)" : "var(--border-color,#e2e8f0)"}`, background: !showInterTab ? "var(--g)" : "#fff", color: !showInterTab ? "#fff" : "var(--text-muted)" }}>
          Mouvements
        </button>
        <button onClick={() => setShowInterTab(true)} style={{ padding: "6px 14px", borderRadius: 8, fontSize: 13, fontWeight: showInterTab ? 700 : 500, cursor: "pointer", border: `1px solid ${showInterTab ? "#7c3aed" : "var(--border-color,#e2e8f0)"}`, background: showInterTab ? "#7c3aed" : "#fff", color: showInterTab ? "#fff" : "var(--text-muted)" }}>
          🔀 Transferts inter-sociétés
        </button>
        {otherCompanies.length > 0 && (
          <button onClick={() => { setInterForm({ dest_company_id: "", source_product_id: "", dest_product_id: "", quantity: "", unit_price: "", reason: "Transfert inter-sociétés", notes: "" }); setInterError(""); setShowInterModal(true); }} style={{ marginLeft: "auto", padding: "6px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", border: "1px solid #7c3aed", background: "#7c3aed22", color: "#7c3aed" }}>
            + Transfert inter-sociétés
          </button>
        )}
      </div>

      {/* Vue Mouvements classiques */}
      {!showInterTab && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            {TYPES.map(t => (
              <button key={t.id} onClick={() => setTypeFilter(t.id)} style={{ padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", border: "var(--border)", background: typeFilter === t.id ? (t.color || "var(--g)") : "#fff", color: typeFilter === t.id ? "#fff" : "var(--text-muted)" }}>
                {t.icon && `${t.icon} `}{t.label}
              </button>
            ))}
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <ProtectedLink url={api.stocks.exportMvts(currentCompanyId)}><button className="btn">⬇️ Export CSV</button></ProtectedLink>
              <button className="btn primary" onClick={() => openModal("entry")}>+ Entrée</button>
              <button className="btn" style={{ background: "#e24b4a22", color: "#e24b4a", border: "1px solid #e24b4a44" }} onClick={() => openModal("exit")}>− Sortie</button>
              <button className="btn" onClick={() => openModal("adjustment")}>🔧 Ajuster</button>
            </div>
          </div>

          <div className="card">
            {loading ? (
              <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>Chargement…</div>
            ) : movements.length === 0 ? (
              <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>Aucun mouvement</div>
            ) : (
              <div>
                {movements.map((m, i) => {
                  const isPositive = ['entry', 'return'].includes(m.type);
                  const showDate = i === 0 || new Date(m.created_at).toDateString() !== new Date(movements[i - 1].created_at).toDateString();
                  const isInterCompany = !!m.inter_transfer_id;
                  return (
                    <div key={m.id}>
                      {showDate && (
                        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", padding: "12px 0 6px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                          {new Date(m.created_at).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
                        </div>
                      )}
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 0", borderBottom: "var(--border)" }}>
                        <div style={{ width: 36, height: 36, borderRadius: "50%", background: isInterCompany ? "#7c3aed18" : `${TYPE_COLOR[m.type]}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
                          {isInterCompany ? "🔀" : TYPE_ICON[m.type]}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ fontWeight: 700, fontSize: 13 }}>{m.product_name}</span>
                            <span style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "monospace" }}>{m.sku}</span>
                            <span style={{ fontSize: 11, padding: "1px 6px", borderRadius: 8, background: isInterCompany ? "#7c3aed18" : `${TYPE_COLOR[m.type]}18`, color: isInterCompany ? "#7c3aed" : TYPE_COLOR[m.type], fontWeight: 700 }}>
                              {isInterCompany ? "Inter-sociétés" : TYPE_LABEL[m.type]}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                            {m.reason || "—"}{m.reference ? ` · Réf. ${m.reference}` : ""}{m.warehouse_name ? ` · ${m.warehouse_name}` : ""}
                          </div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 14, color: isPositive ? "var(--g)" : "var(--red)" }}>
                            {isPositive ? "+" : m.type === "adjustment" ? (m.quantity >= 0 ? "+" : "") : "−"}{Math.abs(m.quantity)} {m.unit}
                          </div>
                          {m.unit_price > 0 && <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{(m.unit_price || 0).toFixed(2)} €/u</div>}
                          <div style={{ fontSize: 10, color: "var(--text-muted)" }}>{new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Vue Transferts inter-sociétés */}
      {showInterTab && (
        <div className="card">
          {interTransfers.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🔀</div>
              <div style={{ fontWeight: 600 }}>Aucun transfert inter-sociétés</div>
              <div style={{ fontSize: 13, marginTop: 6 }}>Utilisez le bouton ci-dessus pour transférer des produits entre sociétés du groupe</div>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--border-color,#e2e8f0)" }}>
                  <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase" }}>Réf.</th>
                  <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase" }}>Source → Dest.</th>
                  <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase" }}>Produit</th>
                  <th style={{ padding: "8px 12px", textAlign: "right", fontWeight: 700, color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase" }}>Qté</th>
                  <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase" }}>Statut</th>
                  <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, color: "var(--text-muted)", fontSize: 11, textTransform: "uppercase" }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {interTransfers.map(t => (
                  <tr key={t.id} style={{ borderBottom: "var(--border)" }}>
                    <td style={{ padding: "10px 12px", fontFamily: "monospace", fontSize: 12, color: "#7c3aed", fontWeight: 700 }}>{t.transfer_ref}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ fontWeight: 600 }}>{t.source_company_name}</span>
                      <span style={{ color: "var(--text-muted)", margin: "0 6px" }}>→</span>
                      <span style={{ fontWeight: 600 }}>{t.dest_company_name}</span>
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ fontWeight: 600 }}>{t.source_product_name}</div>
                      {t.dest_product_name && t.dest_product_name !== t.source_product_name && (
                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>→ {t.dest_product_name}</div>
                      )}
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{t.reason}</div>
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700 }}>{t.quantity}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8, background: `${STATUS_COLOR[t.status]}18`, color: STATUS_COLOR[t.status], fontWeight: 700 }}>
                        {STATUS_LABEL[t.status]}
                      </span>
                    </td>
                    <td style={{ padding: "10px 12px", color: "var(--text-muted)", fontSize: 12 }}>
                      {new Date(t.created_at).toLocaleDateString("fr-FR")}
                      <div style={{ fontSize: 11 }}>{t.created_by_name}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modal nouveau mouvement classique */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h3 className="modal-title">{TYPE_ICON[mvType]} Nouveau mouvement — {TYPE_LABEL[mvType]}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
              {["entry", "exit", "adjustment", "return", "transfer"].map(t => (
                <button key={t} type="button" onClick={() => setMvType(t)} style={{ padding: "4px 10px", borderRadius: 16, fontSize: 11, fontWeight: 600, cursor: "pointer", border: `1px solid ${TYPE_COLOR[t]}`, background: mvType === t ? TYPE_COLOR[t] : `${TYPE_COLOR[t]}15`, color: mvType === t ? "#fff" : TYPE_COLOR[t] }}>
                  {TYPE_ICON[t]} {TYPE_LABEL[t]}
                </button>
              ))}
            </div>
            {error && <div className="alert danger" style={{ marginBottom: 12 }}>{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Produit *</label>
                <select className="form-control" required value={form.product_id} onChange={e => setForm(f => ({ ...f, product_id: e.target.value }))}>
                  <option value="">— Choisir un produit —</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.sku}) — {p.stock_current} {p.unit}</option>)}
                </select>
              </div>

              {mvType !== "transfer" && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{mvType === "adjustment" ? "Nouveau stock *" : "Quantité *"}</label>
                    <input className="form-control" type="number" step="0.001" required min="0" placeholder="0"
                      value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} />
                    {mvType === "adjustment" && selectedProduct && (
                      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                        Stock actuel : {selectedProduct.stock_current} {selectedProduct.unit} →
                        Différence : {(parseFloat(form.quantity) || 0) - selectedProduct.stock_current >= 0 ? "+" : ""}{((parseFloat(form.quantity) || 0) - selectedProduct.stock_current).toFixed(2)}
                      </div>
                    )}
                  </div>
                  {["entry", "return"].includes(mvType) && (
                    <div className="form-group">
                      <label className="form-label">Prix unitaire HT (€)</label>
                      <input className="form-control" type="number" step="0.01" placeholder="0.00"
                        value={form.unit_price} onChange={e => setForm(f => ({ ...f, unit_price: e.target.value }))} />
                    </div>
                  )}
                </div>
              )}

              {mvType === "transfer" && (
                <>
                  <div className="form-group">
                    <label className="form-label">Quantité *</label>
                    <input className="form-control" type="number" step="0.001" required min="0.001" placeholder="0"
                      value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Entrepôt source *</label>
                      <select className="form-control" required value={form.from_warehouse_id} onChange={e => setForm(f => ({ ...f, from_warehouse_id: e.target.value }))}>
                        <option value="">— Entrepôt source —</option>
                        {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Entrepôt destination *</label>
                      <select className="form-control" required value={form.to_warehouse_id} onChange={e => setForm(f => ({ ...f, to_warehouse_id: e.target.value }))}>
                        <option value="">— Entrepôt destination —</option>
                        {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                      </select>
                    </div>
                  </div>
                </>
              )}

              {mvType === "entry" && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">N° de lot</label>
                    <input className="form-control" placeholder="LOT-XXXXX (auto)" value={form.batch_number} onChange={e => setForm(f => ({ ...f, batch_number: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Fournisseur</label>
                    <select className="form-control" value={form.supplier_id} onChange={e => setForm(f => ({ ...f, supplier_id: e.target.value }))}>
                      <option value="">— Aucun —</option>
                      {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {mvType !== "transfer" && warehouses.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Entrepôt</label>
                  <select className="form-control" value={form.warehouse_id} onChange={e => setForm(f => ({ ...f, warehouse_id: e.target.value }))}>
                    <option value="">— Par défaut —</option>
                    {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Motif</label>
                  <input className="form-control" placeholder={`${TYPE_LABEL[mvType]} standard`} value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Référence</label>
                  <input className="form-control" placeholder="N° commande, BL…" value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} />
                </div>
              </div>

              <div className="btn-group" style={{ justifyContent: "flex-end", marginTop: 8 }}>
                <button type="button" className="btn" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn primary" disabled={saving} style={{ background: TYPE_COLOR[mvType] }}>
                  {saving ? "Enregistrement…" : `Enregistrer ${TYPE_LABEL[mvType]}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal transfert inter-sociétés */}
      {showInterModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowInterModal(false)}>
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h3 className="modal-title">🔀 Transfert inter-sociétés</h3>
              <button className="modal-close" onClick={() => setShowInterModal(false)}>✕</button>
            </div>
            <div style={{ padding: "10px 12px", background: "#7c3aed10", border: "1px solid #7c3aed30", borderRadius: 8, marginBottom: 16, fontSize: 12, color: "#7c3aed" }}>
              Les produits sont déduits du stock de la société actuelle et crédités dans la société de destination.
              La traçabilité complète est conservée (référence <code>TIS-XXXXXX</code>).
            </div>
            {interError && <div className="alert danger" style={{ marginBottom: 12 }}>{interError}</div>}
            <form onSubmit={handleInterSubmit}>
              <div className="form-group">
                <label className="form-label">Société destination *</label>
                <select className="form-control" required value={interForm.dest_company_id} onChange={e => setInterForm(f => ({ ...f, dest_company_id: e.target.value, dest_product_id: "" }))}>
                  <option value="">— Choisir la société —</option>
                  {otherCompanies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Produit source (société actuelle) *</label>
                  <select className="form-control" required value={interForm.source_product_id} onChange={e => setInterForm(f => ({ ...f, source_product_id: e.target.value }))}>
                    <option value="">— Choisir un produit —</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.sku}) — {p.stock_current} {p.unit}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Produit destination (optionnel)</label>
                  <select className="form-control" value={interForm.dest_product_id} onChange={e => setInterForm(f => ({ ...f, dest_product_id: e.target.value }))} disabled={!interForm.dest_company_id}>
                    <option value="">— Même produit / en attente —</option>
                    {destProducts.map(p => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
                  </select>
                  {!interForm.dest_company_id && <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>Sélectionnez d'abord la société destination</div>}
                  {interForm.dest_company_id && !interForm.dest_product_id && <div style={{ fontSize: 11, color: "#ba7517", marginTop: 4 }}>Transfert en statut "En attente" jusqu'à réception confirmée</div>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Quantité *</label>
                  <input className="form-control" type="number" step="0.001" required min="0.001" placeholder="0"
                    value={interForm.quantity} onChange={e => setInterForm(f => ({ ...f, quantity: e.target.value }))} />
                  {interForm.source_product_id && (() => {
                    const p = products.find(p => p.id === parseInt(interForm.source_product_id));
                    return p ? <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>Stock disponible : {p.stock_current} {p.unit}</div> : null;
                  })()}
                </div>
                <div className="form-group">
                  <label className="form-label">Prix unitaire (€) — optionnel</label>
                  <input className="form-control" type="number" step="0.01" placeholder="Prix FIFO si vide"
                    value={interForm.unit_price} onChange={e => setInterForm(f => ({ ...f, unit_price: e.target.value }))} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Motif</label>
                  <input className="form-control" placeholder="Transfert inter-sociétés" value={interForm.reason} onChange={e => setInterForm(f => ({ ...f, reason: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <input className="form-control" placeholder="Informations complémentaires…" value={interForm.notes} onChange={e => setInterForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
              </div>

              <div className="btn-group" style={{ justifyContent: "flex-end", marginTop: 8 }}>
                <button type="button" className="btn" onClick={() => setShowInterModal(false)}>Annuler</button>
                <button type="submit" className="btn primary" disabled={interSaving} style={{ background: "#7c3aed" }}>
                  {interSaving ? "Enregistrement…" : "Enregistrer le transfert"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
